import React, { useEffect, useState } from "react";
import { PartnerLayout } from "./PartnerLayout";
import { Button } from "../ui/button";
import partnerApi, { getPartner } from "../../lib/partnerApi";
import { toast } from "sonner";
import { Copy, RefreshCw, Eye, EyeOff } from "lucide-react";

export default function PartnerApiKeys() {
  const [partner, setPartner] = useState(getPartner());
  const [revealedKey, setRevealedKey] = useState(null);
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [rotating, setRotating] = useState(false);

  useEffect(() => {
    partnerApi.get("/partner/me").then((r) => setPartner(r.data));
  }, []);

  const rotate = async (kind) => {
    if (!window.confirm(`Rotate ${kind}? The current value will stop working immediately.`)) return;
    setRotating(true);
    try {
      const ep = kind === "api_key" ? "/partner/rotate-api-key" : "/partner/rotate-webhook-secret";
      const r = await partnerApi.post(ep);
      if (kind === "api_key") setRevealedKey(r.data.api_key);
      else setRevealedSecret(r.data.webhook_secret);
      toast.success(`New ${kind} generated — copy it now, you won't see it again.`);
      const fresh = await partnerApi.get("/partner/me");
      setPartner(fresh.data);
    } catch (e) { toast.error("Rotation failed"); }
    finally { setRotating(false); }
  };

  const snippet = partner ? `<iframe
  src="${window.location.origin}/widget?partner_id=${partner.id}&primaryColor=${encodeURIComponent(partner.primary_color || "#4F46FF")}"
  width="420" height="700" frameborder="0"
  allow="camera; clipboard-write"
  style="border-radius:16px;border:0;">
</iframe>` : "";

  if (!partner) return <PartnerLayout title="API Keys"><div className="text-sm text-muted-foreground">Loading…</div></PartnerLayout>;

  return (
    <PartnerLayout title="API Keys" subtitle="Authentication for partner portal access and webhook verification">
      <div className="grid md:grid-cols-2 gap-5">
        <SecretCard
          label="Partner API Key" hint="Used to log into this portal."
          masked={partner.api_key_masked} revealed={revealedKey}
          onRotate={() => rotate("api_key")} rotating={rotating}
          testidPrefix="api-key"
        />
        <SecretCard
          label="Webhook Secret" hint="HMAC signature secret. Verify X-Xchangely-Signature."
          masked={partner.webhook_secret_masked} revealed={revealedSecret}
          onRotate={() => rotate("webhook_secret")} rotating={rotating}
          testidPrefix="webhook-secret"
        />
      </div>

      <div className="xch-card p-5 mt-5" data-testid="snippet-card">
        <div className="font-display text-base font-bold text-foreground mb-1">Embed snippet</div>
        <p className="text-xs text-muted-foreground mb-3">Pre-filled with your partner ID and brand color.</p>
        <pre className="bg-foreground text-white text-[11px] font-mono rounded-xl p-3 overflow-x-auto whitespace-pre-wrap break-all">{snippet}</pre>
        <Button onClick={() => { navigator.clipboard.writeText(snippet); toast.success("Snippet copied"); }}
                className="mt-3 bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                data-testid="snippet-copy-btn">
          <Copy className="w-4 h-4" /> Copy snippet
        </Button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mt-5 text-sm">
        <strong className="text-amber-900">HMAC verification.</strong> <span className="text-amber-800">Every webhook includes <code className="font-mono bg-white/60 px-1 rounded">X-Xchangely-Signature: sha256=&lt;hex&gt;</code>. Compute <code className="font-mono bg-white/60 px-1 rounded">HMAC-SHA256(webhook_secret, timestamp + "." + raw_body)</code> and compare. Reject requests older than 5 minutes.</span>
      </div>
    </PartnerLayout>
  );
}

function SecretCard({ label, hint, masked, revealed, onRotate, rotating, testidPrefix }) {
  const [shown, setShown] = useState(false);
  const display = revealed && shown ? revealed : masked;
  return (
    <div className="xch-card p-5" data-testid={`${testidPrefix}-card`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
      <div className="text-xs text-muted-foreground mt-0.5 mb-3">{hint}</div>
      <div className="flex items-center gap-2 bg-foreground rounded-xl p-2.5 mb-3">
        <div className="font-mono text-xs text-white flex-1 truncate" data-testid={`${testidPrefix}-value`}>{display}</div>
        {revealed && (
          <button onClick={() => setShown(!shown)} className="text-white/70 hover:text-white p-1" data-testid={`${testidPrefix}-toggle`}>
            {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
        {revealed && shown && (
          <button onClick={() => { navigator.clipboard.writeText(revealed); toast.success("Copied"); }} className="text-white/70 hover:text-white p-1" data-testid={`${testidPrefix}-copy`}>
            <Copy className="w-4 h-4" />
          </button>
        )}
      </div>
      <Button variant="outline" onClick={onRotate} disabled={rotating} className="rounded-xl gap-1.5 w-full" data-testid={`${testidPrefix}-rotate`}>
        <RefreshCw className={`w-3.5 h-3.5 ${rotating ? "animate-spin" : ""}`} /> Rotate
      </Button>
    </div>
  );
}
